import { DefaultListParams } from 'src/common/filterTable/types/DefaultListParams';
import { ExportBtn } from 'src/components/Button';
import { useAlerting, useListMappingTransform, usePermission, useTranslation } from 'src/hooks';
import { downloadFiles, getFullApiResponse } from 'src/utils';
import { usePageConfigProviderContext } from '..';
import { Optional } from '../types/OptionalType';

//Default Export Btn Props type
type ExportBtnProps = React.ComponentProps<typeof ExportBtn>;

//Export Button type
type ExportButtonProps<GetListRes> = {
  fileName: string;
  permissionKey: string;
  omitKeys?: string[];
  mapResultToTupleArray: (rows: GetListRes[], omitKeys: string[]) => any[][][];
  exportTableApiFunc: (
    params: DefaultListParams,
    { signal }: { signal: AbortSignal }
  ) => Promise<any>;
} & Optional<ExportBtnProps, 'isShow' | 'onExport'>;

const ExportButton = <FilterFieldsType, GetListRes>(props: ExportButtonProps<GetListRes>) => {
  const { fileName, permissionKey, omitKeys, mapResultToTupleArray, exportTableApiFunc, ...rest } =
    props;

  const { translatePrefix, fields, list, getListParams } = usePageConfigProviderContext<
    FilterFieldsType,
    GetListRes
  >();

  const { alerting } = useAlerting();
  const { tc } = useTranslation(translatePrefix);
  const listMappingTransform = useListMappingTransform('export');
  const { hasPermission } = usePermission();

  const onExport = async () => {
    const params = getListParams(fields);
    const { currentTime, ...cleanParams } = params;
    if (list.count === 0) {
      return alerting('error', tc('no_data_export'));
    }
    const apiFn = (page: number, pageSize: number, signal: AbortSignal) =>
      exportTableApiFunc({ ...cleanParams, page, pageSize }, { signal });
    const rawRes = await getFullApiResponse(apiFn, list.count, true);
    if (rawRes.length === 0) return;
    const res = mapResultToTupleArray(rawRes, omitKeys ?? ['rawData']);
    const output = res.map(listMappingTransform);
    downloadFiles(fileName, output, {});
  };

  return <ExportBtn isShow={hasPermission(permissionKey)} onExport={onExport} {...rest} />;
};

export default ExportButton;
